import React, { useCallback, useContext } from "react";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import { DeleteIcon as UtilsDeleteIcon } from "../svg/UtilsIcons";

import commonTranslations from "../languages/common.json";
import { LanguageContext } from "../context/LanguageContext";

const DraggableItem = ({
  option,
  index,
  moveItem,
  edit,
  setEdit,
  MoveIcon,
  DeleteIcon,
}) => {
  const { lang } = useContext(LanguageContext);
  const t = commonTranslations[lang].common.DragDropOptionList;

  const [, ref] = useDrag({
    type: "OPTION",
    item: { index },
  });

  const [, drop] = useDrop({
    accept: "OPTION",
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  const MoveIco = MoveIcon || (() => <OpenWithIcon style={{ fontSize: 18 }} />);
  const DeleteIco = DeleteIcon || UtilsDeleteIcon;

  const iconButtonStyle = {
    width: "30px",
    height: "30px",
    padding: "0",
    minWidth: "30px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    color: "var(--details-actions-icon-color, var(--cell--color))",
  };

  return (
    <div
      ref={(node) => ref(drop(node))}
      className="form-item"
      key={index}
      style={{
        display: "flex",
        marginTop: "40px",
        gap: "10px",
        alignItems: "center",
        cursor: "grab",
      }}
    >
      <div
        style={{
          display: "flex",
          width: "100%",
          gap: "10px",
          alignItems: "center",
        }}
      >
        <input
          type="number"
          className="add-column"
          placeholder={t.valuePlaceholder}
          id={"value-" + index}
          style={{
            flex: "0.3",
            padding: "4px 10px",
            color: "var(--cell--color)",
            caretColor: "var(--cell--color)",
          }}
          value={option.value}
          onChange={(e) => {
            const updatedOptions = [...edit.props.options];
            updatedOptions[index].value = e.target.value;
            setEdit({ ...edit, props: { ...edit.props, options: updatedOptions } });
          }}
        />

        <input
          type="text"
          className="add-column"
          placeholder={t.titlePlaceholder}
          id={"label-" + index}
          style={{
            flex: "1",
            padding: "4px 10px",
            color: "var(--cell--color)",
            caretColor: "var(--cell--color)",
          }}
          value={option.label}
          onChange={(e) => {
            const updatedOptions = [...edit.props.options];
            updatedOptions[index].label = e.target.value;
            setEdit({ ...edit, props: { ...edit.props, options: updatedOptions } });
          }}
        />

        <button
          className="widget-btn"
          type="button"
          style={iconButtonStyle}
          aria-label={t.moveAriaLabel || "Move option"}
        >
          <MoveIco />
        </button>

        <button
          type="button"
          onClick={() => {
            const updatedOptions = edit.props.options.filter((_, i) => i !== index);
            setEdit({ ...edit, props: { ...edit.props, options: updatedOptions } });
          }}
          className="widget-btn widget-btn-remove"
          style={{
            ...iconButtonStyle,
            color:
              "var(--details-actions-delete-color, var(--details-actions-icon-color, var(--cell--color)))",
          }}
          aria-label={t.deleteAriaLabel || "Delete option"}
        >
          <DeleteIco width="14px" height="14px" color="currentColor" />
        </button>
      </div>
    </div>
  );
};

const DragDropOptionList = ({ edit, setEdit, MoveIcon, DeleteIcon }) => {
  const moveItem = useCallback(
    (fromIndex, toIndex) => {
      const updatedOptions = [...edit.props.options];
      const [movedItem] = updatedOptions.splice(fromIndex, 1);
      updatedOptions.splice(toIndex, 0, movedItem);
      setEdit({ ...edit, props: { ...edit.props, options: updatedOptions } });
    },
    [edit, setEdit]
  );

  return (
    <DndProvider backend={HTML5Backend}>
      {edit.props.options?.map((option, index) => (
        <DraggableItem
          key={index}
          index={index}
          option={option}
          moveItem={moveItem}
          edit={edit}
          setEdit={setEdit}
          MoveIcon={MoveIcon}
          DeleteIcon={DeleteIcon}
        />
      ))}
    </DndProvider>
  );
};

export default DragDropOptionList;
